import { ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface FAQItem {
  question: string
  answer: ReactNode
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What is FinCreator?',
    answer:
      'FinCreator is a platform for financial content creators to publish content, share trading signals and educational material, build a community, and monetize their audience through subscriptions, tips, and a content marketplace.',
  },
  {
    question: 'How do creators get paid?',
    answer: (
      <>
        Payouts are handled through Stripe Connect. Once you connect a Stripe account from your
        Monetization dashboard, earnings from subscriptions, tips, and marketplace sales are paid
        out to your connected bank account.
      </>
    ),
  },
  {
    question: 'Are trading signals investment advice?',
    answer:
      'No. Trading signals and performance data shared on FinCreator are for informational and educational purposes only. They do not constitute investment advice, and past performance does not guarantee future results. See our Terms of Service for the full disclaimer.',
  },
  {
    question: 'How do subscriptions and tips work?',
    answer:
      'Creators can offer paid subscription tiers that unlock premium content and signals. Supporters can also send one-time tips directly to a creator. Both are processed securely through Stripe.',
  },
  {
    question: 'What is the Marketplace?',
    answer:
      'The Marketplace lets creators sell standalone products, such as e-books, courses, or signal packages, to buyers as one-time purchases.',
  },
  {
    question: 'How do I export my data or delete my account?',
    answer: (
      <>
        Visit your{' '}
        <Link to="/settings" className="text-primary-600 hover:underline">
          Settings
        </Link>{' '}
        page to download a copy of your data, manage your privacy and consent preferences, or
        request account deletion.
      </>
    ),
  },
  {
    question: 'How do I contact support?',
    answer:
      'Reach out to our support team at support@fincreator.app and we\'ll get back to you as soon as possible.',
  },
]

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggle = (index: number) => {
    setOpenIndex((current) => (current === index ? null : index))
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Frequently Asked Questions</h1>
        <p className="text-sm text-gray-500 mb-6">
          Have a question that isn't answered here? Contact support at
          support@fincreator.app.
        </p>

        <div className="divide-y">
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index
            return (
              <div key={item.question} className="py-4">
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="text-sm font-medium text-gray-900">{item.question}</span>
                  <ChevronDownIcon
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isOpen && <p className="mt-2 text-sm text-gray-600">{item.answer}</p>}
              </div>
            )
          })}
        </div>

        <p className="text-xs text-gray-400 pt-6 mt-2 border-t">
          See also our{' '}
          <Link to="/terms" className="text-primary-600 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary-600 hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  )
}

export default FAQ
